import {
  COLOUR_THEMES,
  EMBED_THEME_MESSAGE,
  EMBED_THEME_PARAMS,
  EMBED_THEME_STORAGE_KEY,
} from './embedTheme.constants';
import { ColourTheme, EmbedTheme, ThemeMode } from './embedTheme.types';

export const DEFAULT_EMBED_THEME: EmbedTheme = { mode: 'light', theme: 'default' };

const toThemeMode = (value: unknown): ThemeMode => (value === 'dark' ? 'dark' : 'light');

const toColourTheme = (value: unknown): ColourTheme =>
  COLOUR_THEMES.includes(value as ColourTheme)
    ? (value as ColourTheme)
    : DEFAULT_EMBED_THEME.theme;

export const normaliseEmbedTheme = (raw: { mode?: unknown; theme?: unknown }): EmbedTheme => ({
  mode: toThemeMode(raw.mode),
  theme: toColourTheme(raw.theme),
});

const readUrlTheme = (): EmbedTheme | null => {
  const params = new URLSearchParams(window.location.search);
  const mode = params.get(EMBED_THEME_PARAMS.mode);
  const theme = params.get(EMBED_THEME_PARAMS.theme);
  if (mode === null && theme === null) return null;
  return normaliseEmbedTheme({ mode, theme });
};

const readStoredTheme = (): EmbedTheme | null => {
  try {
    const stored = sessionStorage.getItem(EMBED_THEME_STORAGE_KEY);
    return stored ? normaliseEmbedTheme(JSON.parse(stored)) : null;
  } catch {
    return null;
  }
};

export const persistEmbedTheme = (embedTheme: EmbedTheme) => {
  try {
    sessionStorage.setItem(EMBED_THEME_STORAGE_KEY, JSON.stringify(embedTheme));
  } catch {}
};

export const resolveEmbedTheme = (): EmbedTheme =>
  readUrlTheme() ?? readStoredTheme() ?? DEFAULT_EMBED_THEME;

export const applyColourTheme = (theme: ColourTheme) =>
  document.documentElement.setAttribute('data-theme', theme);

export const applyThemeMode = (mode: ThemeMode) =>
  document.documentElement.setAttribute('data-mode', mode);

export const applyEmbedTheme = (embedTheme: EmbedTheme) => {
  persistEmbedTheme(embedTheme);
  applyColourTheme(embedTheme.theme);
};

export const initEmbedTheme = () => applyEmbedTheme(resolveEmbedTheme());

export const readEmbedThemeMessage = (event: MessageEvent): EmbedTheme | null => {
  if (window.parent === window || event.source !== window.parent) return null;
  if (event.data?.type !== EMBED_THEME_MESSAGE) return null;
  return normaliseEmbedTheme(event.data);
};
